import type { ReactNode, Ref } from "react";
import type {
	TextAreaProps as AriaTextAreaProps,
	TextFieldProps as AriaTextFieldProps,
} from "react-aria-components";

import React from "react";
import {
	TextArea as AriaTextArea,
	TextField as AriaTextField,
} from "react-aria-components";

import { HintText } from "@/components/ui/input/hint-text";
import { Label } from "@/components/ui/input/label";
import { cx } from "@/utils/cx";

// Creates a data URL for an SVG resize handle with a given color.
const getResizeHandleBg = (color: string) => {
	return `url(data:image/svg+xml;base64,${btoa(`<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 2L2 10" stroke="${color}" stroke-linecap="round"/><path d="M11 7L7 11" stroke="${color}" stroke-linecap="round"/></svg>`)})`;
};

interface TextAreaBaseProps extends AriaTextAreaProps {
	/** Error message displayed below the textarea */
	ref?: Ref<HTMLTextAreaElement>;
}

export const TextAreaBase = ({ className, ...props }: TextAreaBaseProps) => {
	return (
		<AriaTextArea
			{...props}
			className={(state) =>
				cx(
					"w-full scroll-py-3 rounded-lg bg-primary px-3.5 py-3 text-md text-primary shadow-xs ring-1 ring-primary transition duration-100 ease-linear ring-inset placeholder:text-placeholder autofill:rounded-lg autofill:text-primary focus:outline-hidden",

					// Resize handle
					"[&::-webkit-resizer]:bg-(image:--resize-handle-bg) [&::-webkit-resizer]:bg-contain dark:[&::-webkit-resizer]:bg-(image:--resize-handle-bg-dark)",

					state.isFocused && !state.isDisabled && "ring-2 ring-brand",
					state.isDisabled && "cursor-not-allowed opacity-50",
					state.isInvalid && "ring-error_subtle",
					state.isInvalid && state.isFocused && "ring-2 ring-error",

					typeof className === "function" ? className(state) : className,
				)
			}
			style={
				{
					"--resize-handle-bg": getResizeHandleBg("#D5D7DA"),
					"--resize-handle-bg-dark": getResizeHandleBg("#373A41"),
				} as React.CSSProperties
			}
		/>
	);
};

TextAreaBase.displayName = "TextAreaBase";

interface TextFieldProps extends AriaTextFieldProps {
	/** Visible width of textarea in columns. */
	cols?: number;
	/** Error message displayed below the textarea */
	errorMessage?: ReactNode;
	/** Whether to hide required indicator from label. */
	hideRequiredIndicator?: boolean;
	/** Helper text displayed below the textarea */
	hint?: ReactNode;
	/** Label text for the textarea */
	label?: string;
	/** Placeholder text. */
	placeholder?: string;
	/** Ref for the textarea wrapper */
	ref?: Ref<HTMLDivElement>;
	/** Visible height of textarea in rows . */
	rows?: number;
	/** Class name for the textarea wrapper */
	textAreaClassName?: TextAreaBaseProps["className"];
	/** Ref for the textarea */
	textAreaRef?: TextAreaBaseProps["ref"];
	/** Tooltip message displayed after the label. */
	tooltip?: string;
}

export const TextArea = ({
	className,
	cols,
	errorMessage,
	hideRequiredIndicator,
	hint,
	label,
	placeholder,
	rows,
	textAreaClassName,
	textAreaRef,
	tooltip,
	...props
}: TextFieldProps) => {
	return (
		<AriaTextField
			{...props}
			className={(state) =>
				cx(
					"group flex h-max w-full flex-col items-start justify-start gap-1.5",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{({ isInvalid, isRequired }) => (
				<>
					{label && (
						<Label
							isRequired={
								hideRequiredIndicator ? !hideRequiredIndicator : isRequired
							}
							tooltip={tooltip}
						>
							{label}
						</Label>
					)}

					<TextAreaBase
						className={textAreaClassName}
						cols={cols}
						placeholder={placeholder}
						ref={textAreaRef}
						rows={rows}
					/>

					{hint && !errorMessage && !isInvalid && (
						<HintText isInvalid={isInvalid}>{hint}</HintText>
					)}
					{errorMessage && isInvalid && (
						<HintText isInvalid={isInvalid}>{errorMessage}</HintText>
					)}
				</>
			)}
		</AriaTextField>
	);
};

TextArea.displayName = "TextArea";
